import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Layers, Sparkles, Link2, X, CheckCheck } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { ProvenanceBadge } from "@/components/Badges";
import { wordCount } from "@/lib/format";

const EvidencePicker = ({ section, sources, onChange, locked }) => {
  const [pick, setPick] = useState("");
  const ev = section.evidence || [];
  const add = () => {
    const s = sources.find((x) => `${x.type}:${x.ref}` === pick);
    if (s && !ev.some((e) => e.ref === s.ref)) onChange([...ev, { type: s.type, ref: s.ref, label: s.label }]);
    setPick("");
  };
  return (
    <div className="mt-3">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="caption">Evidence</span>
        {!ev.length && <span className="text-xs text-slate-400">None linked</span>}
        {ev.map((e) => (
          <span key={e.ref} className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5 text-xs" data-testid={`evidence-chip-${section.key}-${e.ref}`}>
            <Link2 className="h-3 w-3" />{e.label}
            {!locked && <button aria-label={`Remove ${e.label}`} onClick={() => onChange(ev.filter((x) => x.ref !== e.ref))}><X className="h-3 w-3" /></button>}
          </span>
        ))}
      </div>
      {!locked && (
        <div className="mt-2 flex gap-2">
          <select aria-label="Link evidence" data-testid={`evidence-select-${section.key}`} className="input py-1.5 text-xs" value={pick} onChange={(e) => setPick(e.target.value)}>
            <option value="">Link your information or a document…</option>
            {sources.map((s) => <option key={`${s.type}:${s.ref}`} value={`${s.type}:${s.ref}`}>{s.type === "document" ? "Document" : "Fact"}: {s.label} — {s.value}</option>)}
          </select>
          <button onClick={add} disabled={!pick} data-testid={`evidence-add-${section.key}`} className="btn-ghost btn-sm">Link</button>
        </div>
      )}
    </div>
  );
};

export default function SectionEditor({ app, section, sources, locked, reload }) {
  const [text, setText] = useState(section.content || "");
  const [prov, setProv] = useState(section.provenance);
  const [dirty, setDirty] = useState(false);
  const [ai, setAi] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setText(section.content || "");
    setProv(section.provenance);
  }, [section.content, section.provenance]);
  const url = `/applications/${app.id}/sections/${section.key}`;
  const save = async (content = text, provenance = prov === "empty" || prov === "user_reviewed" ? "user" : prov, evidence) => {
    try {
      await api.put(url, { content, provenance, ...(evidence ? { evidence } : {}) });
      setDirty(false);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const compose = async () => {
    const { data } = await api.post(`${url}/compose`);
    if (!data.text) return toast.error("No answers available yet for this section");
    await save(data.text, "assembled", [...(section.evidence || []), ...data.evidence.filter((e) => !(section.evidence || []).some((x) => x.ref === e.ref))]);
    toast.success(data.missing.length ? `Assembled. Missing: ${data.missing.join(", ")}` : "Assembled from your answers");
  };
  const suggest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`${url}/suggest`);
      setAi(data.text);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const words = wordCount(text);
  const over = section.word_limit && words > section.word_limit;
  return (
    <section className="card p-6" data-testid={`section-${section.key}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div><h2 className="font-semibold text-[#0B192C]">{section.title}</h2>{section.guidance && <p className="mt-0.5 text-xs text-slate-500">{section.guidance}</p>}</div>
        <div className="flex items-center gap-2"><ProvenanceBadge source={prov} /><span data-testid={`wordcount-${section.key}`} className={`font-mono text-xs ${over ? "font-bold text-red-600" : "text-slate-500"}`}>{words}{section.word_limit ? ` / ${section.word_limit}` : ""} words</span></div>
      </div>
      <textarea aria-label={section.title} data-testid={`section-input-${section.key}`} rows={6} disabled={locked} className="input mt-3" value={text} onChange={(e) => { setText(e.target.value); setDirty(true); }} />
      {!locked && (
        <div className="mt-2 flex flex-wrap gap-2">
          <button onClick={() => save()} disabled={!dirty} data-testid={`section-save-${section.key}`} className="btn-teal btn-sm">Save</button>
          {section.sources?.length > 0 && <button onClick={compose} data-testid={`section-compose-${section.key}`} className="btn-ghost btn-sm"><Layers className="h-3.5 w-3.5" />Assemble from my answers</button>}
          <button onClick={suggest} disabled={busy} data-testid={`section-ai-${section.key}`} className="btn-ghost btn-sm"><Sparkles className="h-3.5 w-3.5" />{busy ? "Drafting…" : "AI suggestion"}</button>
          {prov === "ai_suggestion" && <button onClick={() => api.post(`${url}/confirm`).then(reload)} data-testid={`section-confirm-${section.key}`} className="btn-ghost btn-sm text-teal-700"><CheckCheck className="h-3.5 w-3.5" />I have reviewed and confirm this text</button>}
        </div>
      )}
      {ai !== null && (
        <div className="mt-3 rounded-md border border-teal-200 bg-teal-50 p-4" data-testid={`ai-suggestion-${section.key}`}>
          <ProvenanceBadge source="ai" />
          <p className="mt-2 whitespace-pre-line text-sm text-slate-800">{ai}</p>
          <p className="mt-2 text-xs text-teal-900">Uses only facts you provided. Replace every [EVIDENCE NEEDED] marker before approval.</p>
          <div className="mt-3 flex gap-2">
            <button onClick={() => { save(ai, "ai_suggestion"); setAi(null); }} data-testid={`ai-insert-${section.key}`} className="btn-teal btn-sm">Insert as AI suggestion</button>
            <button onClick={() => setAi(null)} data-testid={`ai-discard-${section.key}`} className="btn-ghost btn-sm">Discard</button>
          </div>
        </div>
      )}
      <EvidencePicker section={section} sources={sources} locked={locked} onChange={(ev) => save(text, prov === "empty" ? "user" : prov, ev)} />
    </section>
  );
}
